import React, { useEffect, useState } from "react";
import { BookmarkPlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { cloneProgram } from "../../lib/api";

/**
 * "Save as template" — copies this athlete's program (or a week range of
 * it) into the generic program library so it can be assigned to others.
 */
export const SaveAsTemplateDialog = ({ open, onOpenChange, program, onSaved }) => {
  const weekCount = program?.weeks?.length || 0;
  const [title, setTitle] = useState("");
  const [fromWeek, setFromWeek] = useState(1);
  const [toWeek, setToWeek] = useState(weekCount);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = (program?.title || "Program").replace(/^.*?\s[—-]\s/, "");
    setTitle(`${base} — Template`);
    setFromWeek(1);
    setToWeek(weekCount);
  }, [open, program?.title, weekCount]);

  const lo = Math.max(1, Math.min(Number(fromWeek) || 1, weekCount));
  const hi = Math.max(lo, Math.min(Number(toWeek) || weekCount, weekCount));
  const n = hi - lo + 1;

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || saving) return;
    setSaving(true);
    try {
      const created = await cloneProgram(program.id, { title: title.trim(), from_week: lo, to_week: hi });
      toast.success(`Saved "${created.title}" to your library (${n} week${n === 1 ? "" : "s"})`);
      onOpenChange(false);
      onSaved?.(created);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save the template");
    } finally {
      setSaving(false);
    }
  };

  const numCls = "w-20 border border-[var(--tir-border)] focus:border-[var(--tir-ink)] px-2 py-1.5 text-sm font-mono-tir focus:outline-none bg-white";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="save-template-dialog">
        <form onSubmit={submit} className="space-y-5">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Save as template</DialogTitle>
            <DialogDescription>
              Copies the weeks you choose into your program library as a reusable template. The athlete&apos;s program is untouched.
            </DialogDescription>
          </DialogHeader>

          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">Template name</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="save-template-title"
              className="w-full border border-[var(--tir-border)] focus:border-[var(--tir-ink)] px-3 py-2 text-sm focus:outline-none bg-white"
              placeholder="e.g. Hyrox Build — 12 weeks"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">Weeks to include</label>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[var(--tir-muted)]">W</span>
              <input type="number" min={1} max={weekCount} value={fromWeek} onChange={(e) => setFromWeek(e.target.value)} data-testid="save-template-from" className={numCls} />
              <span className="text-[var(--tir-muted)]">to</span>
              <span className="text-[var(--tir-muted)]">W</span>
              <input type="number" min={1} max={weekCount} value={toWeek} onChange={(e) => setToWeek(e.target.value)} data-testid="save-template-to" className={numCls} />
              <span className="text-[var(--tir-muted)] text-xs ml-1" data-testid="save-template-count">
                {n} of {weekCount} week{weekCount === 1 ? "" : "s"}
              </span>
            </div>
            <div className="flex gap-3 mt-2">
              <button type="button" onClick={() => { setFromWeek(1); setToWeek(weekCount); }} className="text-[10px] tracking-[0.2em] uppercase text-[var(--tir-burgundy)] font-semibold" data-testid="save-template-all">All weeks</button>
            </div>
            {lo !== 1 && (
              <p className="text-[11px] text-[var(--tir-muted)] mt-2 italic">The template will be renumbered to start at Week 1.</p>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-[var(--tir-border)]">
            <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)]" data-testid="save-template-cancel">Cancel</button>
            <button
              type="submit"
              disabled={saving || !title.trim() || weekCount === 0}
              data-testid="save-template-submit"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--tir-burgundy)] hover:bg-[var(--tir-burgundy-dark)] disabled:opacity-50 text-white text-[11px] tracking-[0.22em] uppercase font-semibold"
            >
              <BookmarkPlus size={13} /> {saving ? "Saving…" : "Save to library"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
